import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Code,
  Badge,
} from "@chakra-ui/react";
import { API_BASE_URL } from "../../framework/api";
import {
  checkHealth,
  fetchHealthCheck,
  testSentinelError,
  testErrorAbort,
  testErrorRuntime,
  testErrorPanic,
  testEvents,
  testProgress as testProgressAPI,
} from "../../lib/api/health";
import type { EventsTestResponse } from "../../lib/api/health";
import { ApiErrorWithStack } from "../../lib/api/types";
import type { HealthCheckResponse } from "../../lib/api/types/health";
import * as Sentry from "@sentry/react";

interface HealthDashboardProps {
  token: string;
}

type TestResult = {
  result: HealthCheckResponse | null;
  error: string | null;
  traceId: string | null;
  loading: boolean;
};

export default function HealthDashboard({ token }: HealthDashboardProps) {
  const [quickCheck, setQuickCheck] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [fullCheck, setFullCheck] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [sentinelError, setSentinelError] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [errorAbort, setErrorAbort] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [errorRuntime, setErrorRuntime] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [errorPanic, setErrorPanic] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [clientError, setClientError] = useState<TestResult>({
    result: null,
    error: null,
    traceId: null,
    loading: false,
  });

  const [eventsTest, setEventsTest] = useState<{
    result: EventsTestResponse | null;
    error: string | null;
    loading: boolean;
    eventReceived: boolean;
  }>({
    result: null,
    error: null,
    loading: false,
    eventReceived: false,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [testProgress, setTestProgress] = useState<{
    active: boolean;
    percent: number;
    message: string;
  }>({
    active: false,
    percent: 0,
    message: "",
  });

  // WebSocket setup for receiving test events
  useEffect(() => {
    const wsUrl = `${API_BASE_URL}/ws?token=${encodeURIComponent(token)}`;
    let ws: WebSocket | null = null;
    let keepAlive: ReturnType<typeof setInterval> | null = null;
    let isCleaningUp = false;

    const connectTimer = setTimeout(() => {
      if (isCleaningUp) return;

      try {
        ws = new WebSocket(wsUrl.replace(/^http/, "ws").replace(/^https/, "wss"));
        wsRef.current = ws;

        keepAlive = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);

        ws.onopen = () => {
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.Type === "test_event") {
              setEventsTest((prev) => ({
                ...prev,
                eventReceived: true,
              }));
            }
            if (message.Type === "test_progress") {
              const data = message.Object;
              setTestProgress({
                active: true,
                percent: data.percent || 0,
                message: data.message || "",
              });
            }
          } catch (err) {
            console.error("Failed to parse WebSocket message:", err);
          }
        };

        ws.onerror = () => {
          setWsConnected(false);
        };

        ws.onclose = () => {
          setWsConnected(false);
        };
      } catch (err) {
        console.error("Failed to create WebSocket:", err);
        setWsConnected(false);
      }
    }, 100);

    return () => {
      isCleaningUp = true;
      clearTimeout(connectTimer);
      if (keepAlive) clearInterval(keepAlive);
      if (ws) {
        ws.close();
      }
      wsRef.current = null;
    };
  }, [token]);

  const runTest = async (
    testFn: () => Promise<HealthCheckResponse>,
    setState: (state: TestResult) => void
  ) => {
    setState({ result: null, error: null, traceId: null, loading: true });
    try {
      const result = await testFn();
      setState({ result, error: null, traceId: null, loading: false });
    } catch (error) {
      if (error instanceof ApiErrorWithStack) {
        setState({
          result: null,
          error: error.message,
          traceId: error.traceId || null,
          loading: false,
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setState({
          result: null,
          error: errorMessage,
          traceId: null,
          loading: false,
        });
      }
    }
  };

  const runQuickHealthCheck = () => {
    runTest(() => checkHealth(token), setQuickCheck);
  };

  const runFullHealthCheck = () => {
    runTest(() => fetchHealthCheck(token), setFullCheck);
  };

  const runSentinelErrorTest = () => {
    runTest(() => testSentinelError(token), setSentinelError);
  };

  const runErrorAbortTest = () => {
    runTest(() => testErrorAbort(token), setErrorAbort);
  };

  const runErrorRuntimeTest = () => {
    runTest(() => testErrorRuntime(token), setErrorRuntime);
  };

  const runErrorPanicTest = () => {
    runTest(() => testErrorPanic(token), setErrorPanic);
  };

  const runClientErrorTest = () => {
    setClientError({ result: null, error: null, traceId: null, loading: true });
    try {
      // Simulate a client-side error by accessing an undefined object property
      const undefinedObject: any = null;
      undefinedObject.someProperty.deepProperty;
      setClientError({ result: null, error: "Unexpected success", traceId: null, loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setClientError({
        result: null,
        error: errorMessage,
        traceId: null,
        loading: false,
      });
      // Re-throw to let Sentry capture it
      Sentry.captureException(error);
      throw error;
    }
  };

  const runEventsTest = async () => {
    setEventsTest({ result: null, error: null, loading: true, eventReceived: false });
    try {
      const result = await testEvents(token);
      setEventsTest({ result, error: null, loading: false, eventReceived: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setEventsTest({
        result: null,
        error: errorMessage,
        loading: false,
        eventReceived: false,
      });
    }
  };

  const runProgressTest = async () => {
    setTestProgress({ active: true, percent: 0, message: "Starting server progress test..." });
    try {
      await testProgressAPI(token);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setTestProgress({ active: false, percent: 0, message: `Error: ${errorMessage}` });
    }
  };

  const getStatusColor = (result: HealthCheckResponse | null, error: string | null) => {
    if (error) return "red";
    if (!result) return "gray";
    if (result.Status === "ok") return "green";
    return "orange";
  };

  const getStatusText = (result: HealthCheckResponse | null, error: string | null, loading: boolean) => {
    if (loading) return "Checking...";
    if (error) return "Error";
    if (!result) return "Not checked";
    if (result.Status === "ok") return "Healthy";
    return "Issues";
  };

  return (
    <Box>
      <VStack align="stretch" gap={6}>
        <Box>
          <Text fontSize="xl" fontWeight="bold" mb={4}>
            Health & Error Checks
          </Text>
        </Box>

        <Box borderTop="1px" borderColor="gray.600" my={4} />

        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            Health Checks
          </Text>
          <VStack align="stretch" gap={3}>
            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="blue"
                onClick={runQuickHealthCheck}
                loading={quickCheck.loading}
                minW="240px"
              >
                Quick Health Check
              </Button>
              <Badge colorPalette={getStatusColor(quickCheck.result, quickCheck.error)}>
                {getStatusText(quickCheck.result, quickCheck.error, quickCheck.loading)}
              </Badge>
            </HStack>
            {quickCheck.error && (
              <Box mb={2} bg="red.700" px={3} py={2} borderRadius="md">
                <Text fontSize="sm" color="white" fontWeight="bold" mb={1}>
                  Error
                </Text>
                <Text fontSize="sm" color="white">
                  {quickCheck.error}
                </Text>
                {quickCheck.result && quickCheck.result.Status && (
                  <Text fontSize="xs" color="gray.300" mt={1}>
                    Status: {quickCheck.result.Status}
                  </Text>
                )}
              </Box>
            )}
            {quickCheck.result && !quickCheck.error && (
              <Box p={3} bg="gray.700" borderRadius="md" fontSize="sm">
                <Text fontWeight="bold" color="white">Status: {quickCheck.result.Status}</Text>
                {quickCheck.result.Environment && <Text color="gray.200">Environment: {quickCheck.result.Environment}</Text>}
                {quickCheck.result.PipelineVersion && <Text color="gray.200">Pipeline Version: {quickCheck.result.PipelineVersion}</Text>}
                {quickCheck.result.TestedSubsystems && quickCheck.result.TestedSubsystems.length > 0 && (
                  <Text color="gray.200">Tested: {quickCheck.result.TestedSubsystems.join(", ")}</Text>
                )}
              </Box>
            )}
            {quickCheck.error && quickCheck.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{quickCheck.traceId}</Code>
                </Text>
              </Box>
            )}

            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="green"
                onClick={runFullHealthCheck}
                loading={fullCheck.loading}
                minW="240px"
              >
                Full System Check
              </Button>
              <Badge colorPalette={getStatusColor(fullCheck.result, fullCheck.error)}>
                {getStatusText(fullCheck.result, fullCheck.error, fullCheck.loading)}
              </Badge>
            </HStack>
            {fullCheck.error && (
              <Box mb={2} bg="red.700" px={3} py={2} borderRadius="md">
                <Text fontSize="sm" color="white" fontWeight="bold" mb={1}>
                  Error
                </Text>
                <Text fontSize="sm" color="white">
                  {fullCheck.error}
                </Text>
                {fullCheck.result && fullCheck.result.Status && (
                  <Text fontSize="xs" color="gray.300" mt={1}>
                    Status: {fullCheck.result.Status}
                  </Text>
                )}
              </Box>
            )}
            {fullCheck.result && !fullCheck.error && (
              <Box p={3} bg="gray.700" borderRadius="md" fontSize="sm">
                <Text fontWeight="bold" color="white">Status: {fullCheck.result.Status}</Text>
                {fullCheck.result.Environment && <Text color="gray.200">Environment: {fullCheck.result.Environment}</Text>}
                {fullCheck.result.PipelineVersion && <Text color="gray.200">Pipeline Version: {fullCheck.result.PipelineVersion}</Text>}
                {fullCheck.result.TestedSubsystems && fullCheck.result.TestedSubsystems.length > 0 && (
                  <Text color="gray.200">Tested: {fullCheck.result.TestedSubsystems.join(", ")}</Text>
                )}
              </Box>
            )}
            {fullCheck.error && fullCheck.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{fullCheck.traceId}</Code>
                </Text>
              </Box>
            )}
            {fullCheck.result && fullCheck.result.TestedSubsystems && (
              <Box p={3} bg="blue.900" borderRadius="md" fontSize="sm">
                <Text fontWeight="bold" color="white">Subsystems Tested:</Text>
                <VStack align="start" gap={1} mt={2}>
                  {fullCheck.result.TestedSubsystems.map((subsystem) => (
                    <Text key={subsystem} color="gray.200">• {subsystem}</Text>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>

        <Box borderTop="1px" borderColor="gray.600" my={4} />

        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            Events Subsystem
          </Text>
          <VStack align="stretch" gap={3}>
            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="purple"
                onClick={runEventsTest}
                loading={eventsTest.loading}
                minW="240px"
              >
                Send Test Event
              </Button>
              <Badge colorPalette={wsConnected ? "green" : "orange"}>
                WebSocket {wsConnected ? "Connected" : "Disconnected"}
              </Badge>
            </HStack>
            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="teal"
                onClick={runProgressTest}
                minW="240px"
              >
                Test WebSocket Progress
              </Button>
            </HStack>
            {eventsTest.error && (
              <Box mb={2} bg="red.700" px={3} py={2} borderRadius="md">
                <Text fontSize="sm" color="white" fontWeight="bold" mb={1}>
                  Error
                </Text>
                <Text fontSize="sm" color="white">
                  {eventsTest.error}
                </Text>
              </Box>
            )}
            {eventsTest.result && !eventsTest.error && (
              <Box p={3} bg="gray.700" borderRadius="md" fontSize="sm">
                <Text fontWeight="bold" color="white">Status: {eventsTest.result.Status}</Text>
                <Text color="gray.200">Message: {eventsTest.result.Message}</Text>
                <Text color="gray.200">User ID: {eventsTest.result.UserId}</Text>
                {eventsTest.result.EventType && (
                  <Text color="gray.200">Event Type: {eventsTest.result.EventType}</Text>
                )}
              </Box>
            )}
            {eventsTest.eventReceived && (
              <Box mb={2} bg="green.700" px={3} py={2} borderRadius="md">
                <Text fontSize="sm" color="white" fontWeight="bold">
                  ✓ Test event received via WebSocket!
                </Text>
              </Box>
            )}
            {testProgress.active && (
              <Box bg="gray.800" p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.200" mb={2}>
                  {testProgress.message}
                </Text>
                <Box bg="gray.700" borderRadius="full" h="3" overflow="hidden">
                  <Box
                    h="100%"
                    bg="teal.400"
                    width={`${testProgress.percent}%`}
                    transition="width 0.3s ease"
                  />
                </Box>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {testProgress.percent.toFixed(0)}%
                </Text>
              </Box>
            )}
          </VStack>
        </Box>

        <Box borderTop="1px" borderColor="gray.600" my={4} />

        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            Error Tests
          </Text>
          <VStack align="stretch" gap={3}>
            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="orange"
                onClick={runSentinelErrorTest}
                loading={sentinelError.loading}
                minW="240px"
              >
                Test Sentinel Error
              </Button>
              <Badge colorPalette={getStatusColor(sentinelError.result, sentinelError.error)}>
                {getStatusText(sentinelError.result, sentinelError.error, sentinelError.loading)}
              </Badge>
            </HStack>
            {sentinelError.error && sentinelError.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{sentinelError.traceId}</Code>
                </Text>
              </Box>
            )}

            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="orange"
                onClick={runErrorAbortTest}
                loading={errorAbort.loading}
                minW="240px"
              >
                Test Server Error (Abort)
              </Button>
              <Badge colorPalette={getStatusColor(errorAbort.result, errorAbort.error)}>
                {getStatusText(errorAbort.result, errorAbort.error, errorAbort.loading)}
              </Badge>
            </HStack>
            {errorAbort.error && errorAbort.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{errorAbort.traceId}</Code>
                </Text>
              </Box>
            )}

            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="orange"
                onClick={runErrorRuntimeTest}
                loading={errorRuntime.loading}
                minW="240px"
              >
                Test Server Error (Runtime)
              </Button>
              <Badge colorPalette={getStatusColor(errorRuntime.result, errorRuntime.error)}>
                {getStatusText(errorRuntime.result, errorRuntime.error, errorRuntime.loading)}
              </Badge>
            </HStack>
            {errorRuntime.error && errorRuntime.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{errorRuntime.traceId}</Code>
                </Text>
              </Box>
            )}

            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="orange"
                onClick={runErrorPanicTest}
                loading={errorPanic.loading}
                minW="240px"
              >
                Test Server Error (Panic)
              </Button>
              <Badge colorPalette={getStatusColor(errorPanic.result, errorPanic.error)}>
                {getStatusText(errorPanic.result, errorPanic.error, errorPanic.loading)}
              </Badge>
            </HStack>
            {errorPanic.error && errorPanic.traceId && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  TraceId: <Code fontSize="xs" bg="transparent" color="gray.300">{errorPanic.traceId}</Code>
                </Text>
              </Box>
            )}

            <HStack gap={3}>
              <Button
                size="sm"
                colorPalette="red"
                onClick={runClientErrorTest}
                loading={clientError.loading}
                minW="240px"
              >
                Test Client Error
              </Button>
              <Badge colorPalette={getStatusColor(clientError.result, clientError.error)}>
                {getStatusText(clientError.result, clientError.error, clientError.loading)}
              </Badge>
            </HStack>
            {clientError.error && (
              <Box mb={2} bg="blue.700" px={3} py={2} borderRadius="md">
                <Text fontSize="xs" color="white">
                  Client-side errors are captured in Sentry but may not have a server traceId.
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
